<script setup lang="ts">
import { ref, watch } from 'vue';
import { useAdminEditsStore } from '@/stores/adminEdits';
import type { Member } from '@/stores/villages';
import {
  EditIcon,
  CheckIcon,
  XIcon,
  AlertCircleIcon,
  SendIcon,
  InfoCircleIcon,
} from 'vue-tabler-icons';

const props = defineProps<{
  modelValue: boolean;
  member: Member | null;
  villageId: string;
  villageName: string;
  familyId: string;
  headName: string;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
  (e: 'submitted'): void;
}>();

const adminEditsStore = useAdminEditsStore();

const formData = ref<Partial<Member>>({});
const submitterName = ref('');
const submitterPhone = ref('');
const note = ref('');

const errorMessage = ref<string | null>(null);
const successMessage = ref<string | null>(null);
const isSubmitting = ref(false);

watch(
  () => props.member,
  (newMem) => {
    if (newMem) {
      formData.value = {
        name: newMem.name || '',
        nameE: newMem.nameE || '',
        age: newMem.age || 0,
        sex: newMem.sex || 'Male',
        role: newMem.role || 'Self',
        occupation: newMem.occupation || '',
        currentResidence: newMem.currentResidence || '',
        education: newMem.education || '',
        maternal: newMem.maternal || '',
        dob: newMem.dob || '',
        contact: newMem.contact || '',
      };
      errorMessage.value = null;
      successMessage.value = null;
    }
  },
  { immediate: true }
);

function closeDialog() {
  emit('update:modelValue', false);
  errorMessage.value = null;
  successMessage.value = null;
}

function handleSubmit() {
  errorMessage.value = null;
  successMessage.value = null;

  if (!props.member) return;

  if (!submitterName.value.trim()) {
    errorMessage.value = 'કૃપા કરીને તમારું નામ (સુધારો સૂચવનારનું નામ) લખો.';
    return;
  }

  const cleanSubPhone = submitterPhone.value.replace(/\D/g, '');
  if (!cleanSubPhone || cleanSubPhone.length < 10) {
    errorMessage.value = 'કૃપા કરીને તમારો માન્ય ૧૦ અંકનો મોબાઇલ નંબર લખો.';
    return;
  }

  isSubmitting.value = true;
  try {
    const res = adminEditsStore.submitEditRequest({
      villageId: props.villageId,
      villageName: props.villageName,
      familyId: props.familyId,
      headName: props.headName,
      member: props.member,
      updatedData: formData.value,
      submitterName: submitterName.value,
      submitterPhone: submitterPhone.value,
      note: note.value,
    });

    if (!res.success) {
      errorMessage.value = res.message;
    } else {
      successMessage.value = res.message;
      submitterName.value = '';
      submitterPhone.value = '';
      note.value = '';
      setTimeout(() => {
        emit('submitted');
        closeDialog();
      }, 1800);
    }
  } catch (err: any) {
    errorMessage.value = err?.message || 'સુધારો સબમિટ કરવામાં ભૂલ આવી.';
  } finally {
    isSubmitting.value = false;
  }
}
</script>

<template>
  <v-dialog
    :model-value="modelValue"
    max-width="650"
    scrollable
    persistent
    @update:model-value="(val) => emit('update:modelValue', val)"
  >
    <v-card class="rounded-xl overflow-hidden shadow-24">
      <!-- Modal Header -->
      <v-card-item class="bg-primary text-white py-3 px-4">
        <div class="d-flex align-center justify-space-between w-100">
          <div class="d-flex align-center" style="gap: 10px;">
            <v-avatar size="34" color="white" class="text-primary font-weight-bold">
              <EditIcon size="18" class="text-primary" />
            </v-avatar>
            <div>
              <div class="text-subtitle-1 font-weight-black line-height-1">
                વિગતો સુધારવા વિનંતી (Suggest Correction)
              </div>
              <div class="text-caption text-white opacity-80">
                {{ member?.name }} • {{ villageName }}
              </div>
            </div>
          </div>

          <v-btn icon size="small" variant="text" color="white" @click="closeDialog">
            <XIcon size="18" />
          </v-btn>
        </div>
      </v-card-item>

      <v-card-text class="pa-4 pa-sm-5">
        <!-- Notice banner -->
        <v-alert
          type="info"
          variant="tonal"
          density="compact"
          class="rounded-lg mb-4 text-caption font-weight-medium"
        >
          <div class="d-flex align-center" style="gap: 6px;">
            <InfoCircleIcon size="16" class="flex-shrink-0" />
            <span>
              <strong>ધ્યાન આપો:</strong> તમે સૂચવેલો સુધારો સીધો ડિરેક્ટરીમાં બદલાશે નહીં. એડમિન દ્વારા ચકાસીને (Verify કરી) મંજૂર કર્યા પછી જ ડિરેક્ટરીમાં અપડેટ થશે.
            </span>
          </div>
        </v-alert>

        <!-- Feedback Alert Messages -->
        <v-alert
          v-if="errorMessage"
          type="error"
          variant="tonal"
          density="compact"
          class="rounded-lg mb-3"
          closable
          @click:close="errorMessage = null"
        >
          {{ errorMessage }}
        </v-alert>

        <v-alert
          v-if="successMessage"
          type="success"
          variant="flat"
          density="compact"
          class="rounded-lg mb-3 text-white font-weight-bold"
        >
          <div class="d-flex align-center" style="gap: 6px;">
            <CheckIcon size="18" />
            {{ successMessage }}
          </div>
        </v-alert>

        <!-- Member Form Fields -->
        <div class="text-subtitle-2 font-weight-bold text-primary mb-2">
          ૧. સભ્યની નવી વિગતો:
        </div>

        <v-row dense class="mb-3">
          <v-col cols="12" sm="6">
            <v-text-field
              v-model="formData.name"
              label="ગુજરાતી નામ (Name)"
              density="compact"
              variant="outlined"
              hide-details="auto"
              class="mb-2"
            />
          </v-col>
          <v-col cols="12" sm="6">
            <v-text-field
              v-model="formData.nameE"
              label="અંગ્રેજી નામ (English Name)"
              density="compact"
              variant="outlined"
              hide-details="auto"
              class="mb-2"
            />
          </v-col>

          <v-col cols="6" sm="4">
            <v-text-field
              v-model="formData.age"
              label="ઉંમર (Age)"
              type="number"
              density="compact"
              variant="outlined"
              hide-details="auto"
              class="mb-2"
            />
          </v-col>
          <v-col cols="6" sm="4">
            <v-select
              v-model="formData.sex"
              :items="['Male', 'Female']"
              label="જાતિ (Gender)"
              density="compact"
              variant="outlined"
              hide-details="auto"
              class="mb-2"
            />
          </v-col>
          <v-col cols="12" sm="4">
            <v-text-field
              v-model="formData.role"
              label="સંબંધ (Role)"
              density="compact"
              variant="outlined"
              hide-details="auto"
              class="mb-2"
            />
          </v-col>

          <v-col cols="12" sm="6">
            <v-text-field
              v-model="formData.contact"
              label="મોબાઇલ નંબર (Mobile)"
              density="compact"
              variant="outlined"
              hide-details="auto"
              class="mb-2"
              placeholder="9876543210"
            />
          </v-col>
          <v-col cols="12" sm="6">
            <v-text-field
              v-model="formData.occupation"
              label="વ્યવસાય (Occupation)"
              density="compact"
              variant="outlined"
              hide-details="auto"
              class="mb-2"
            />
          </v-col>

          <v-col cols="12" sm="6">
            <v-text-field
              v-model="formData.education"
              label="અભ્યાસ (Education)"
              density="compact"
              variant="outlined"
              hide-details="auto"
              class="mb-2"
            />
          </v-col>
          <v-col cols="12" sm="6">
            <v-text-field
              v-model="formData.maternal"
              label="મોસાળ (Maternal Village/Name)"
              density="compact"
              variant="outlined"
              hide-details="auto"
              class="mb-2"
            />
          </v-col>

          <v-col cols="12" sm="6">
            <v-text-field
              v-model="formData.dob"
              label="જન્મ તારીખ (DOB)"
              density="compact"
              variant="outlined"
              hide-details="auto"
              class="mb-2"
              placeholder="DD/MM/YYYY"
            />
          </v-col>
          <v-col cols="12" sm="6">
            <v-text-field
              v-model="formData.currentResidence"
              label="હાલનું સરનામું (Residence)"
              density="compact"
              variant="outlined"
              hide-details="auto"
              class="mb-2"
            />
          </v-col>
        </v-row>

        <!-- Submitter Details -->
        <div class="text-subtitle-2 font-weight-bold text-primary mb-2 border-t pt-3">
          ૨. તમારી વિગતો (ચકાસણી માટે):
        </div>

        <v-row dense>
          <v-col cols="12" sm="6">
            <v-text-field
              v-model="submitterName"
              label="તમારું પૂરું નામ *"
              placeholder="દા.ત. રમેશભાઈ ભોજાણી"
              density="compact"
              variant="outlined"
              hide-details="auto"
              class="mb-2"
            />
          </v-col>
          <v-col cols="12" sm="6">
            <v-text-field
              v-model="submitterPhone"
              label="તમારો મોબાઇલ નંબર *"
              placeholder="૧૦ અંકનો મોબાઇલ નંબર"
              density="compact"
              variant="outlined"
              hide-details="auto"
              class="mb-2"
            />
          </v-col>
          <v-col cols="12">
            <v-textarea
              v-model="note"
              label="સુધારા અંગે કોઈ નોંધ / કારણ (વૈકલ્પિક)"
              placeholder="જેમ કે: નવો મોબાઇલ નંબર છે, સરનામું બદલાયું છે..."
              rows="2"
              density="compact"
              variant="outlined"
              hide-details="auto"
              class="mb-1"
            />
          </v-col>
        </v-row>
      </v-card-text>

      <v-divider />

      <v-card-actions class="pa-4 d-flex justify-end" style="gap: 8px;">
        <v-btn variant="tonal" color="secondary" rounded="lg" @click="closeDialog">
          રદ કરો
        </v-btn>
        <v-btn
          color="primary"
          variant="flat"
          rounded="lg"
          :loading="isSubmitting"
          @click="handleSubmit"
        >
          <SendIcon size="16" class="mr-1" />
          સુધારો સબમિટ કરો
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
